import carnationImage from '../assets/carnation.png';
import currencyImage from '../assets/currency.png';
import portfolioImage from '../assets/personal.png';
import splitRightImage from '../assets/splitright.png';
import sushiInvoiceImage from '../assets/sushi-invoice.png';
import miniQuantImage from '../assets/mini-quant-lab.svg';
import charlieImage from '../assets/charlie-intelligence.svg';
import healthcareReferralImage from '../assets/healthcare-referral-demo.png';
import aiCapabilityImage from '../assets/ai-capability-readiness.png';
import gcpModernizationImage from '../assets/gcp-support-modernization.png';
import dataEngineeringPrepImage from '../assets/data-engineering-prep.png';
import appTrackerImage from '../assets/apptracker.png';
import contactsAppImage from '../assets/contacts-app.png';
import threeDPortfolioImage from '../assets/galen-3d-portfolio.png';

export const deploymentFilters = [
  { value: 'all', label: 'All projects' },
  { value: 'live', label: 'Live now' },
  { value: 'not-live', label: 'Not deployed' },
  { value: 'private', label: 'Private' },
];

export const projects = [
  {
    slug: 'healthcare-referral-demo',
    title: 'Healthcare Referral Demo Platform',
    image: healthcareReferralImage,
    summary: 'A synthetic healthcare referral operations demo with rules-driven automation, exception routing, and resumable human review.',
    techStack: ['React 19', 'TypeScript', 'Vite', 'Workflow automation', 'Synthetic healthcare data'],
    type: 'Healthcare Demo',
    deploymentState: 'private',
    deploymentLabel: 'Private prototype',
    deploymentDetail: 'Runs locally; no public deployment or public repository.',
    liveUrl: null,
    repoUrl: null,
    blogPath: '/blogPosts/projects/healthcare-referral-demo',
    blogDate: '07/07/2026',
    isNew: true,
    caseStudy: {
      eyebrow: 'Healthcare workflow automation',
      intro: 'Meridian Intake models the messy specialist-referral journey from faxed packet to scheduling-ready patient while keeping humans focused on genuine exceptions.',
      problem: 'Referral teams manually read documents, re-key insurance and demographic data, chase missing information, interpret payer rules, and route work before a patient can be scheduled.',
      approach: 'I built a deterministic six-stage automation pipeline with typed exceptions for missing documents, prior authorisation, and low-confidence extraction. Reviewers can resolve an exception and resume the exact pipeline stage that was blocked.',
      outcomes: [
        'Fourteen synthetic referrals across five specialties create a repeatable demo environment without real PHI.',
        'Live rules change payer and documentation behaviour without changing application code.',
        'A simulated REST console exposes every user action, payload, status, and latency for technical walkthroughs.',
      ],
      reflection: 'The key design decision was making exceptions first-class workflow states. Automation remains useful even when it cannot complete the whole referral without a person.',
    },
  },
  {
    slug: 'ai-capability-readiness',
    title: 'AI Capability Readiness Tool',
    image: aiCapabilityImage,
    summary: 'My thesis prototype for evaluating whether AI-generated HL7 FHIR resources are reliable, safe, and ready for controlled healthcare deployment.',
    techStack: ['React', 'AWS CDK', 'Step Functions', 'Lambda', 'FHIR R4', 'OpenAI API'],
    type: 'Thesis Prototype',
    deploymentState: 'live',
    deploymentLabel: 'Live on AWS',
    deploymentDetail: 'Public CloudFront deployment verified and responding successfully.',
    liveUrl: 'https://dysfhk2gjftpg.cloudfront.net/',
    repoUrl: 'https://github.com/Gal-Y/ai-capability-assessment-tool',
    blogPath: '/blogPosts/projects/ai-capability-readiness',
    blogDate: '30/06/2026',
    isNew: true,
    caseStudy: {
      eyebrow: 'Thesis · HL7/FHIR readiness evaluation',
      intro: 'This is an evaluation layer for clinical AI pipelines, not the production converter itself. It tests whether candidate structured outputs are ready to move toward controlled deployment.',
      problem: 'A system can generate plausible FHIR JSON and still lose clinical facts, produce invalid resources, leak sensitive information, or fail operational requirements.',
      approach: 'The tool classifies HL7 CDA/C-CDA, clinical PDF, FHIR JSON, and text inputs, then combines deterministic checks with model-based evaluation across reliability, clinical coverage, governance, privacy, and operational constraints.',
      outcomes: [
        'Produces Ready, Conditional, or Not Ready decisions with case-level evidence.',
        'Checks CDA-to-FHIR mapping, resource shape, code grounding, PHI containment, and prompt-injection resistance.',
        'Uses an AWS serverless workflow built with API Gateway, S3, DynamoDB, Step Functions, and Python Lambda functions.',
      ],
      reflection: 'The strongest thesis framing came from separating conversion capability from deployment readiness. The evaluator makes risk and evidence visible without overclaiming production interoperability.',
    },
  },
  {
    slug: 'gcp-support-modernization',
    title: 'GCP Support Platform Modernization',
    image: gcpModernizationImage,
    summary: 'A customer-engineering case study that modernizes a legacy support platform with managed Google Cloud services and AI-assisted workflows.',
    techStack: ['Cloud Run', 'Cloud SQL', 'Pub/Sub', 'BigQuery', 'Vertex AI', 'Terraform'],
    type: 'Cloud Case Study',
    deploymentState: 'source',
    deploymentLabel: 'Not publicly deployed',
    deploymentDetail: 'Public source code and local demo; no active public Cloud Run environment.',
    liveUrl: null,
    repoUrl: 'https://github.com/Gal-Y/gcp-support-modernization',
    blogPath: '/blogPosts/projects/gcp-support-modernization',
    blogDate: '28/05/2026',
    isNew: true,
    caseStudy: {
      eyebrow: 'Google Cloud customer engineering',
      intro: 'A realistic discovery-to-demo engagement showing how a VM-hosted support platform can move toward managed compute, eventing, analytics, and AI-assisted triage.',
      problem: 'The fictional current state relies on a VM monolith, local attachments, synchronous processing, manual triage, and limited auditability.',
      approach: 'I designed and implemented a cloud-ready target state using Cloud Run, Cloud SQL, Cloud Storage, Pub/Sub, BigQuery, Vertex AI, Secret Manager, Cloud Logging, and Terraform.',
      outcomes: [
        'Includes both the legacy baseline and a modernized operations console for side-by-side demonstrations.',
        'Provides architecture, migration, security, cost, stakeholder, and demo materials alongside the code.',
        'Runs locally with deterministic fallbacks, while GCP adapters activate when cloud configuration is supplied.',
      ],
      reflection: 'The project taught me to connect service selection to business friction. The architecture is only useful when the migration story, security model, cost, and stakeholder narrative agree.',
    },
  },
  {
    slug: 'galen-3d-portfolio',
    title: '3D Portfolio Experiment',
    image: threeDPortfolioImage,
    summary: 'An experimental Three.js portfolio that turns work, interests, and aspirations into interactive cinematic orbit decks.',
    techStack: ['React', 'Vite', 'Three.js', 'Lucide React'],
    type: 'Creative Experiment',
    deploymentState: 'source',
    deploymentLabel: 'Not publicly deployed',
    deploymentDetail: 'Public source code; currently intended as a local interaction experiment.',
    liveUrl: null,
    repoUrl: 'https://github.com/Gal-Y/galen-3d-portfolio',
    blogPath: '/blogPosts/projects/galen-3d-portfolio',
    blogDate: '03/06/2026',
    isNew: true,
    caseStudy: {
      eyebrow: 'Creative frontend experiment',
      intro: 'I explored how a portfolio could feel like a navigable object rather than a stack of pages, while keeping a readable information panel beside the 3D scene.',
      problem: 'Highly visual portfolios can become hard to navigate or inaccessible when spectacle replaces structure.',
      approach: 'I organised work, resume, interests, and aspirations into orbit decks with selectable cards, cinematic camera movement, and a conventional detail panel for legibility.',
      outcomes: [
        'Created reusable Three.js orbit boards and selectable content cards.',
        'Balanced continuous 3D motion with a stable reading surface.',
        'Kept the experiment isolated from the production portfolio so it could explore a very different interaction model.',
      ],
      reflection: 'The experiment reinforced that novelty should help someone explore the content. A clear detail panel is what makes the 3D interaction usable rather than purely decorative.',
    },
  },
  {
    slug: 'data-engineering-prep',
    title: 'Data Engineering Prep Lab',
    image: dataEngineeringPrepImage,
    summary: 'An interactive practice lab for SQL, pipeline reliability, Python transformations, and technical communication.',
    techStack: ['JavaScript', 'SQL practice', 'Data pipelines', 'GitHub Pages'],
    type: 'Learning Tool',
    deploymentState: 'live',
    deploymentLabel: 'Live on GitHub Pages',
    deploymentDetail: 'Public deployment verified and responding successfully.',
    liveUrl: 'https://gal-y.github.io/quantium-data-engineer-prep/',
    repoUrl: 'https://github.com/Gal-Y/quantium-data-engineer-prep',
    blogPath: '/blogPosts/projects/data-engineering-prep',
    blogDate: '12/05/2026',
    isNew: true,
    caseStudy: {
      eyebrow: 'Interactive learning product',
      intro: 'A browser-based lab built to turn interview preparation into deliberate practice instead of passive reading.',
      problem: 'SQL and pipeline concepts are easy to skim but difficult to recall under interview pressure without repeated explanation and practice.',
      approach: 'The lab combines mental models, source tables, worked examples, visual checklists, typed questions, and review states across four interview pillars.',
      outcomes: [
        'Covers SQL querying, engineering fundamentals, pipeline reliability, Python, and communication.',
        'Uses a rules-based checker to reinforce clause order and scenario reasoning.',
        'Ships as a lightweight GitHub Pages deployment with no backend dependency.',
      ],
      reflection: 'The most useful learning loop is explain, attempt, inspect, and retry. The UI is designed around that cycle rather than presenting another long set of notes.',
    },
  },
  {
    slug: 'apptracker',
    title: 'AppTracker',
    image: appTrackerImage,
    summary: 'A local-first internship tracker with a Kanban pipeline, calendar, tags, full-text search, backups, and import/export.',
    techStack: ['React', 'TypeScript', 'Express', 'SQLite', 'Prisma', 'Tailwind CSS'],
    type: 'Local-first App',
    deploymentState: 'private',
    deploymentLabel: 'Private local app',
    deploymentDetail: 'Runs entirely on-device with local SQLite storage; intentionally not publicly deployed.',
    liveUrl: null,
    repoUrl: null,
    blogPath: '/blogPosts/projects/apptracker',
    blogDate: '11/02/2026',
    isNew: true,
    caseStudy: {
      eyebrow: 'Local-first productivity',
      intro: 'AppTracker keeps internship applications, events, notes, and tags on the user’s own machine without accounts or third-party data services.',
      problem: 'Job tracking spreadsheets become difficult to search, audit, back up, and move through a clear application pipeline.',
      approach: 'I built a React and TypeScript client backed by Express, Prisma, SQLite, and FTS5, with Kanban movement, calendar views, tags, automatic backups, and JSON/CSV portability.',
      outcomes: [
        'Works offline and stores the database in a visible local data directory.',
        'Supports full-text search, status history, import/export, and automatic backup rotation.',
        'Serves the production client and API from one local process after a full build.',
      ],
      reflection: 'Local-first architecture was a product choice, not just a technical choice. It removes account friction and keeps sensitive application notes under the user’s control.',
    },
  },
  {
    slug: 'contacts-app',
    title: 'Contacts App',
    image: contactsAppImage,
    summary: 'A simple deployed React application for browsing and searching contacts.',
    techStack: ['React', 'JavaScript', 'Vercel'],
    type: 'Web Application',
    deploymentState: 'live',
    deploymentLabel: 'Live on Vercel',
    deploymentDetail: 'Public deployment verified and responding successfully.',
    liveUrl: 'https://contacts-app-pi-three.vercel.app/',
    repoUrl: 'https://github.com/Gal-Y/contacts-app',
    blogPath: '/blogPosts/projects/contacts-app',
    blogDate: '20/11/2024',
    isNew: true,
    caseStudy: {
      eyebrow: 'Deployed React application',
      intro: 'A focused contact browsing interface built while learning how component state, filtering, and deployment fit together in a small React product.',
      problem: 'The project needed a fast, clear way to browse and search a contact list without introducing unnecessary interface complexity.',
      approach: 'I used a compact React interface with a search-first layout, simple empty states, and a lightweight Vercel deployment.',
      outcomes: [
        'Provides responsive contact filtering in a minimal single-page interface.',
        'Established an early end-to-end workflow from React development to a public deployment.',
        'Remains available as a live reference for a small, focused frontend build.',
      ],
      reflection: 'Small projects are useful for learning product restraint. The interface succeeds when the search task remains obvious and immediate.',
    },
  },
  {
    slug: 'carnation-joy-care',
    title: 'CarnationJoyCare Website',
    image: carnationImage,
    summary: 'A responsive service website for an aged care and disability support provider.',
    techStack: ['AWS', 'HTML', 'CSS', 'JavaScript'],
    type: 'Website',
    deploymentState: 'live',
    deploymentLabel: 'Live on AWS',
    deploymentDetail: 'CloudFront deployment verified; the custom domain is not currently resolving, so this links directly to CloudFront.',
    liveUrl: 'https://d3keqnb7ojd1nq.cloudfront.net/',
    repoUrl: 'https://github.com/Gal-Y/Carnation-Joy-Website',
    blogPath: '/blogPosts/cjoy',
  },
  {
    slug: 'currency-converter',
    title: 'Currency Converter',
    image: currencyImage,
    summary: 'A responsive currency conversion application using a public exchange-rate API and interactive charts.',
    techStack: ['AWS S3', 'CloudFront', 'JavaScript', 'Chart.js', 'Frankfurter API'],
    type: 'Application',
    deploymentState: 'live',
    deploymentLabel: 'Live on AWS',
    deploymentDetail: 'Public CloudFront deployment verified and responding successfully.',
    liveUrl: 'https://d6kev5zk7z0ut.cloudfront.net/',
    repoUrl: 'https://github.com/Gal-Y/Currency-converter',
    blogPath: '/blogPosts/ccon',
  },
  {
    slug: 'personal-portfolio',
    title: 'Personal Portfolio',
    image: portfolioImage,
    summary: 'This React portfolio for projects, experience, technical writing, and professional contact.',
    techStack: ['AWS Amplify', 'React', 'JavaScript', 'Tailwind CSS'],
    type: 'Portfolio',
    deploymentState: 'live',
    deploymentLabel: 'Live on AWS',
    deploymentDetail: 'Public deployment verified and responding successfully.',
    liveUrl: 'https://www.galen-yuan.com/',
    repoUrl: 'https://github.com/Gal-Y/personal-portfolio',
    blogPath: '/blogPosts/pport',
  },
  {
    slug: 'splitright',
    title: 'Expense Splitter',
    image: splitRightImage,
    summary: 'A cloud-native expense splitting application designed around container orchestration and autoscaling.',
    techStack: ['AWS EKS', 'React', 'Python', 'Flask'],
    type: 'Application',
    deploymentState: 'live',
    deploymentLabel: 'Live on AWS',
    deploymentDetail: 'Public CloudFront deployment verified and responding successfully.',
    liveUrl: 'https://d29uafkfzy7eak.cloudfront.net/',
    repoUrl: 'https://github.com/Gal-Y/SplitRight',
    blogPath: '/blogPosts/sright',
  },
  {
    slug: 'sushi-invoice',
    title: 'Sushi-Invoice',
    image: sushiInvoiceImage,
    summary: 'An AI-assisted invoice workflow backed by serverless AWS services.',
    techStack: ['React', 'Node.js', 'API Gateway', 'Lambda', 'DynamoDB'],
    type: 'Application',
    deploymentState: 'live',
    deploymentLabel: 'Live on AWS',
    deploymentDetail: 'Public CloudFront deployment verified and responding successfully.',
    liveUrl: 'https://d31n0bqlviw94c.cloudfront.net/',
    repoUrl: 'https://github.com/Gal-Y/Sushi-Invoice',
    blogPath: '/blogPosts/sinvoice',
  },
  {
    slug: 'mini-quant-lab',
    title: 'Mini Quant Lab',
    image: miniQuantImage,
    summary: 'An educational predictive-investing lab built with Python data tooling and a browser-based interface.',
    techStack: ['Python', 'NumPy', 'Pandas', 'Chart.js', 'API Gateway'],
    type: 'Application',
    deploymentState: 'live',
    deploymentLabel: 'Live on AWS',
    deploymentDetail: 'Public CloudFront deployment verified and responding successfully.',
    liveUrl: 'https://d3mf2mf3fqpn.cloudfront.net/',
    repoUrl: 'https://github.com/Gal-Y/numpy-learning-mini-quant-lab',
    blogPath: '/blogPosts/mquant',
  },
  {
    slug: 'charlie-intelligence',
    title: 'Charlie Intelligence Platform',
    image: charlieImage,
    summary: 'A team-built intelligence platform using AWS infrastructure, a React frontend, and Python services.',
    techStack: ['AWS CDK', 'React', 'Vite', 'Python', 'DynamoDB', 'OpenAI API'],
    type: 'Platform',
    deploymentState: 'source',
    deploymentLabel: 'Not publicly deployed',
    deploymentDetail: 'Public source code is available; there is no active public application environment.',
    liveUrl: null,
    repoUrl: 'https://github.com/shawn-wjh/SENG3011_W13A_Charlie',
    blogPath: null,
  },
];

export const newProjectBlogPosts = projects
  .filter((project) => project.isNew)
  .map((project) => ({
    id: `project-${project.slug}`,
    title: `${project.title} Build Notes`,
    description: project.summary,
    date: project.blogDate,
    image: project.image,
    link: project.blogPath,
    deploymentState: project.deploymentState,
    deploymentLabel: project.deploymentLabel,
  }));

export const getProjectBySlug = (slug) => projects.find((project) => project.slug === slug && project.caseStudy);
